import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from '../../../../config/axios';
import UserContext from '../../../../contexts/UserContext';
import {
  InputDiv,
  Label,
  Input,
  SearchOptions,
  Option,
  VectorIcon,
} from '../Select/select-style';
import vector from '../../../../assets/equipment/vector.svg';
import ReactDOM from 'react-dom';

interface ItemType {
  id: number;
  [key: string]: any;
}

interface UserSelectProps<T extends ItemType> {
  apiUrl: string;
  displayField: keyof T;
  value: T | null;
  onChange: (item: T | null) => void;
  profileId?: number;
  profileName?: string;
  permission?: string;
}

const UserSelect = <T extends ItemType>({
  apiUrl,
  displayField,
  value,
  onChange,
  profileId,
  profileName,
  permission,
}: UserSelectProps<T>) => {
  const { user } = useContext(UserContext);
  const [items, setItems] = useState<T[]>([]);
  const [allSellers, setAllSellers] = useState<T[]>([]);
  const [inputItem, setInputItem] = useState<string>(
    (value?.[displayField] as string) || '',
  );
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    setInputItem((value?.[displayField] as string) || '');
  }, [value, displayField]);

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        
        if (!token) {
          console.error('Token não encontrado');
          setError('Token não encontrado');
          setAllSellers([]);
          setItems([]);
          return;
        }
        
        const params: any = { loadQuantity: 1000 };
        if (permission) {
          params.permission = permission;
        }
        
        console.log('Buscando usuários em:', apiUrl);
        console.log('Parâmetros:', params);
        
        const response = await axios.get(apiUrl, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log('Resposta da API:', response.data);

        let data = response.data;
        
        if (data.data) {
          data = data.data;
        } else if (data.users) {
          data = data.users;
        }

        if (Array.isArray(data)) {
          let filteredItems = data;
          
          console.log('Total de usuários carregados:', filteredItems.length);
          
          if (profileId !== undefined) {
            filteredItems = data.filter(
              (item) => (item as any).profileId === profileId,
            );
            console.log('Filtrado por profileId:', filteredItems.length);
          } else if (profileName !== undefined) {
            filteredItems = data.filter(
              (item) => (item as any).profile?.name === profileName,
            );
            console.log('Filtrado por profileName:', filteredItems.length);
          }
          
          if (filteredItems.length === 0) {
            console.warn('Nenhum usuário encontrado após filtros');
          }
          
          setAllSellers(filteredItems);
          setItems(filteredItems);
        } else {
          console.error('Resposta da API não é um array:', typeof data);
          setError('Formato de resposta inválido');
          setAllSellers([]);
          setItems([]);
        }
      } catch (error: any) {
        console.error('Erro ao buscar usuários:', error);
        console.error('Detalhes do erro:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        
        setError(error.response?.data?.message || error.message || 'Erro ao carregar usuários');
        setAllSellers([]);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSellers();
  }, [apiUrl, profileId, profileName, permission]);

  useEffect(() => {
    if (showOptions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showOptions, inputRef]);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputItem(newValue);
    setSelectedIndex(-1);

    const filterCriteria = newValue.toLowerCase();
    const filteredItems = allSellers.filter((item) =>
      (item[displayField] as string)?.toLowerCase()?.includes(filterCriteria),
    );
    setItems(filteredItems);

    setShowOptions(true);
    if (newValue === '') {
      onChange(null);
    }
  };

  const handleSelectItem = (item: T) => {
    setInputItem(item[displayField] as string);
    setShowOptions(false);
    onChange(item);
  };

  const handleSetOptionByKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex !== -1 && items[selectedIndex]) {
        handleSelectItem(items[selectedIndex]);
      } else {
        setShowOptions(false);
        onChange(null);
      }
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      const selectedItem = items.find(
        (item) =>
          (item[displayField] as string)?.toLowerCase() ===
          inputItem.toLowerCase(),
      );

      if (!selectedItem) {
        setInputItem('');
        onChange(null);
      }
      setShowOptions(false);
    }, 150);
  };

  return (
    <InputDiv>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Input
          ref={inputRef}
          type='text'
          value={inputItem}
          onFocus={() => setShowOptions(true)}
          onChange={handleChangeInput}
          onKeyDown={handleSetOptionByKey}
          onBlur={handleInputBlur}
          placeholder={loading ? 'Carregando...' : error ? 'Erro ao carregar' : 'Selecione'}
        />
        <VectorIcon src={vector} alt="dropdown arrow" />
      </div>
      {showOptions &&
        items.length > 0 &&
        ReactDOM.createPortal(
          <SearchOptions
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            {items.map((s, index) => (
              <Option
                key={s.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectItem(s)}
                isSelected={index === selectedIndex}
              >
                {s[displayField] as React.ReactNode}
              </Option>
            ))}
          </SearchOptions>,
          document.body,
        )}
      {showOptions && loading && (
        <SearchOptions
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
        >
          <Option>Carregando usuários...</Option>
        </SearchOptions>
      )}
      {showOptions && error && items.length === 0 && !loading && (
        <SearchOptions
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
        >
          <Option style={{ color: 'red' }}>{error}</Option>
        </SearchOptions>
      )}
    </InputDiv>
  );
};

export default UserSelect;